import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createSelectors } from './selectors';
import type { AuthCache, AuthUser } from '@/lib/auth/types';

const STORE_NAME = 'ahafood.new-auth';
const CACHE_KEY = 'ahafood_auth_cache';

interface NewAuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  customerId: string | null;

  /** Backwards-compat: derived từ user, expose top-level cho callsites cũ. */
  phoneNumber?: string;
  loginType?: 'facebook' | 'ahamove' | 'otp';
}

interface NewAuthActions {
  setUser: (user: AuthUser | null) => void;
  setCustomerId: (id: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  logout: () => void;
}

const initialState: NewAuthState = {
  user: null,
  isAuthenticated: false,
  // Default true để tránh flash "Chưa đăng nhập" trước khi hydrateAndSync
  // kịp đọc cookie + decode JWT. Switch false ngay khi hydrate xong.
  isLoading: true,
  customerId: null,
  phoneNumber: undefined,
  loginType: undefined,
};

const store = create<NewAuthState & NewAuthActions>()(
  persist(
    (set) => ({
      ...initialState,
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          phoneNumber: user?.phone ?? user?.phoneNumber,
          loginType: user?.loginType ?? (user ? 'ahamove' : undefined),
        }),
      setCustomerId: (customerId) =>
        set((s) => ({
          customerId,
          user: s.user
            ? { ...s.user, cunstomerId: customerId ?? undefined }
            : null,
        })),
      setIsLoading: (isLoading) => set({ isLoading }),
      logout: () => set({ ...initialState }),
    }),
    {
      name: STORE_NAME,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        customerId: state.customerId,
        phoneNumber: state.phoneNumber,
        loginType: state.loginType,
      }),
    }
  )
);

export const useNewAuthStore = createSelectors(store);

/* ------------ Auth cache (customerId + tokenHash) ------------ */

export function readAuthCache(): AuthCache | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthCache;
  } catch {
    return null;
  }
}

export function writeAuthCache(cache: AuthCache): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore */
  }
}

export function clearAuthCache(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
